import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';
import { NovaSectionLoader, NovaButtonLoader } from './ui/NovaLoader';
import '../CSS/PaiePage.css';

const PaiePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [fichePaie, setFichePaie] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [users, setUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [openDialog, setOpenDialog] = useState(false);
  const [ficheToDelete, setFicheToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [datePaye, setDatePaye] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const getAuthHeaders = () => {
    const token = getToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const normalizeRows = (data) => Array.isArray(data) ? data : [];

  const formatDate = (value) => {
    if (!value) return '—';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('fr-FR');
  };

  const getSelectedUserLabel = () => {
    if (!isAdmin) return 'Mes fiches de paie';

    if (selectedUser === 'all') return 'Tous les utilisateurs';

    const found = users.find((user) => String(user.id) === String(selectedUser));

    return found ? `${found.nom} ${found.prenom}` : 'Utilisateur sélectionné';
  };

  const stats = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const total = fichePaie.length;

    const yearCount = fichePaie.filter((fiche) => {
      const d = new Date(fiche.Date_Paye);
      return !Number.isNaN(d.getTime()) && d.getFullYear() === currentYear;
    }).length;

    const monthCount = fichePaie.filter((fiche) => {
      const d = new Date(fiche.Date_Paye);
      return !Number.isNaN(d.getTime()) && d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    }).length;

    return { total, yearCount, monthCount };
  }, [fichePaie]);

  const fetchFichePaie = useCallback(async (userIdOverride = null, options = {}) => {
    const silent = Boolean(options.silent);
    const target = isAdmin ? (userIdOverride || selectedUser) : currentUserId;

    if (!target) return;

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
      if (isAdmin) setLoadingAdmin(true);
    }

    try {
      const endpoint = target === 'all'
        ? `${apiUrl}/fiche-paie/all`
        : `${apiUrl}/fiche-paie/${target}`;

      const response = await axios.get(endpoint, getAuthHeaders());

      setFichePaie(normalizeRows(response.data));
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Erreur chargement fiches de paie:', error);

      if (!silent) {
        setNotification({
          open: true,
          message: "Erreur lors du chargement des fiches de paie.",
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
      setLoadingAdmin(false);
      setRefreshing(false);
    }
  }, [isAdmin, selectedUser, currentUserId]);

  useEffect(() => {
    const init = async () => {
      const token = getToken();

      if (!token) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);

        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        const admin = decodedToken.role === 'ADMIN';
        const userId = decodedToken.user_id;

        setIsAdmin(admin);
        setCurrentUserId(userId);

        let initialSelected = admin ? (location.state?.userId || 'all') : userId;

        if (admin) {
          const usersResponse = await axios.get(`${apiUrl}/users-declarations`, getAuthHeaders());
          setUsers(normalizeRows(usersResponse.data));
        }

        setSelectedUser(initialSelected);

        const endpoint = initialSelected === 'all'
          ? `${apiUrl}/fiche-paie/all`
          : `${apiUrl}/fiche-paie/${initialSelected}`;

        const ficheResponse = await axios.get(endpoint, getAuthHeaders());

        setFichePaie(normalizeRows(ficheResponse.data));
        setLastRefresh(new Date());
      } catch (error) {
        console.error('Erreur initialisation fiche de paie:', error);
        setNotification({
          open: true,
          message: "Erreur lors du chargement de la page fiche de paie.",
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate, location.state?.userId]);

  useEffect(() => {
    if (!currentUserId) return;

    const interval = setInterval(() => {
      if (!openAddDialog && !openDialog && !uploading && !deleting) {
        fetchFichePaie(null, { silent: true });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentUserId, selectedUser, openAddDialog, openDialog, uploading, deleting, fetchFichePaie]);

  const handleUserSelection = async (userId) => {
    setSelectedUser(userId);
    await fetchFichePaie(userId, { silent: false });
  };

  const handleDownload = async (ficheId) => {
    try {
      const response = await axios.get(`${apiUrl}/fiche-paie/download/${ficheId}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });

      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = urlBlob;
      link.setAttribute('download', `fiche_de_paie_${ficheId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error('Erreur téléchargement fiche de paie:', error);
      setNotification({
        open: true,
        message: 'Erreur lors du téléchargement de la fiche de paie.',
        severity: 'error'
      });
    }
  };

  const confirmDelete = (ficheId) => {
    setFicheToDelete(ficheId);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!ficheToDelete) return;

    setDeleting(true);

    try {
      await axios.delete(`${apiUrl}/fiche-paie/${ficheToDelete}`, getAuthHeaders());

      setFichePaie((current) => current.filter((fiche) => fiche.Id_Paye !== ficheToDelete));

      setNotification({
        open: true,
        message: 'Fiche de paie supprimée avec succès.',
        severity: 'success'
      });

      setOpenDialog(false);
      setFicheToDelete(null);

      fetchFichePaie(null, { silent: true });
    } catch (error) {
      console.error('Erreur suppression fiche de paie:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de la suppression de la fiche de paie.',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenAdd = () => {
    if (isAdmin && (!selectedUser || selectedUser === 'all')) {
      setNotification({
        open: true,
        message: 'Veuillez sélectionner un utilisateur avant d’ajouter une fiche de paie.',
        severity: 'error'
      });
      return;
    }

    setOpenAddDialog(true);
  };

  const handleUploadPaie = async () => {
    if (!datePaye || !pdfFile) {
      setNotification({
        open: true,
        message: 'Veuillez remplir tous les champs.',
        severity: 'error'
      });
      return;
    }

    if (isAdmin && (!selectedUser || selectedUser === 'all')) {
      setNotification({
        open: true,
        message: 'Veuillez sélectionner un utilisateur.',
        severity: 'error'
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();

      formData.append('Id_Utilisateur', isAdmin ? selectedUser : currentUserId);
      formData.append('Date_Paye', datePaye);
      formData.append('url_paie', pdfFile);
      formData.append('nom', pdfFile.name);

      await axios.post(`${apiUrl}/fiche-paie`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setNotification({
        open: true,
        message: 'Fiche de paie ajoutée avec succès.',
        severity: 'success'
      });

      setOpenAddDialog(false);
      setDatePaye('');
      setPdfFile(null);

      fetchFichePaie(null, { silent: true });
    } catch (error) {
      console.error('Erreur ajout fiche de paie:', error);
      setNotification({
        open: true,
        message: "Erreur lors de l'ajout de la fiche de paie.",
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <LayoutDashboard>
      <main className="paie-page">
        <section className="paie-hero">
          <div>
            <span className="paie-pill">Documents RH</span>
            <h1>Fiches de paie</h1>
            <p>
              Consultez, ajoutez, téléchargez et gérez les fiches de paie depuis un espace rapide et clair.
            </p>
          </div>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              className="paie-refresh-btn"
              variant="outlined"
              startIcon={refreshing ? <NovaButtonLoader label="Actualisation" /> : <RefreshRoundedIcon />}
              onClick={() => fetchFichePaie(null, { silent: true })}
              disabled={refreshing}
            >
              {refreshing ? 'Actualisation' : 'Rafraîchir'}
            </Button>

            <Button
              className="paie-add-btn"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={handleOpenAdd}
            >
              Ajouter une fiche
            </Button>
          </Stack>
        </section>

        <Container maxWidth="xl" className="paie-container-modern">
          <section className="paie-stats-grid">
            <article className="paie-stat-card">
              <span>Total</span>
              <strong>{stats.total}</strong>
              <small>fiche(s) disponible(s)</small>
            </article>

            <article className="paie-stat-card">
              <span>Cette année</span>
              <strong>{stats.yearCount}</strong>
              <small>fiche(s) en {new Date().getFullYear()}</small>
            </article>

            <article className="paie-stat-card">
              <span>Ce mois</span>
              <strong>{stats.monthCount}</strong>
              <small>fiche(s) du mois</small>
            </article>

            <article className="paie-stat-card">
              <span>Vue active</span>
              <strong>{isAdmin ? 'Admin' : 'Moi'}</strong>
              <small>{getSelectedUserLabel()}</small>
            </article>
          </section>

          <section className="paie-toolbar-card">
            <div>
              <Typography className="paie-section-title">Liste des fiches</Typography>
            </div>

            {isAdmin && (
              <TextField
                select
                label="Utilisateur"
                value={selectedUser ?? 'all'}
                onChange={(e) => handleUserSelection(e.target.value)}
                className="paie-user-select"
              >
                <MenuItem value="all">Tous les utilisateurs</MenuItem>

                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.nom} {user.prenom}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </section>

          <section className="paie-table-card">
            {loading || loadingAdmin ? (
              <NovaSectionLoader label="Chargement des fiches de paie" helper="Merci de patienter..." />
            ) : (
              <TableContainer className="paie-table-container">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date de paiement</TableCell>
                      <TableCell>Date de dépôt</TableCell>
                      <TableCell>Fichier</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="center">Télécharger</TableCell>
                      <TableCell align="center">Supprimer</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {fichePaie.map((fiche) => (
                      <TableRow key={fiche.Id_Paye}>
                        <TableCell>{formatDate(fiche.Date_Paye)}</TableCell>
                        <TableCell>{formatDate(fiche.date_de_depot)}</TableCell>
                        <TableCell>
                          <div className="paie-file-name">
                            <PictureAsPdfRoundedIcon />
                            <span>{fiche.nom || 'Fiche de paie.pdf'}</span>
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="Disponible" className="paie-chip-ok" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            className="paie-action-download"
                            variant="outlined"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={() => handleDownload(fiche.Id_Paye)}
                          >
                            Télécharger
                          </Button>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            className="paie-action-delete"
                            variant="outlined"
                            startIcon={<DeleteRoundedIcon />}
                            onClick={() => confirmDelete(fiche.Id_Paye)}
                          >
                            Supprimer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}

                    {fichePaie.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <div className="paie-empty-state">
                            <PictureAsPdfRoundedIcon />
                            <strong>Aucune fiche disponible</strong>
                            <span>Aucune fiche de paie n’est encore disponible pour cette sélection.</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </section>
        </Container>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Confirmation de suppression</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir supprimer cette fiche de paie ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              className="paie-confirm-delete"
              disabled={deleting}
            >
              {deleting ? <NovaButtonLoader label="Suppression" /> : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openAddDialog}
          onClose={() => !uploading && setOpenAddDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Ajouter une fiche de paie</DialogTitle>

          <DialogContent>
            <Box className="paie-add-form">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date de paiement"
                  value={datePaye ? dayjs(datePaye) : null}
                  onChange={(newValue) => {
                    if (newValue) {
                      setDatePaye(newValue.format('YYYY-MM-DD'));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </LocalizationProvider>

              <Button
                variant="outlined"
                component="label"
                className="paie-file-picker"
              >
                Choisir le PDF
                <input
                  type="file"
                  hidden
                  accept=".pdf,application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                />
              </Button>

              {pdfFile && (
                <div className="paie-selected-file">
                  <PictureAsPdfRoundedIcon />
                  <span>{pdfFile.name}</span>
                </div>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpenAddDialog(false)}
              variant="outlined"
              disabled={uploading}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={handleUploadPaie}
              disabled={uploading}
            >
              {uploading ? <NovaButtonLoader label="Ajout" /> : 'Ajouter'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={2600}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={notification.severity} className="paie-snackbar-alert">
            {notification.message}
          </Alert>
        </Snackbar>
      </main>
    </LayoutDashboard>
  );
};

export default PaiePage;
